/**
 * The Digs and Welds tabs on a PF job, and the dig history on a site
 * (fieldiq-api#1219, PF-3a). Additive panels: each reads its own list
 * (`GET /digs?jobId=` / `?siteId=`, `GET /welds?jobId=`) and opens the record's
 * own page. A dig is planned from here (dispatch roles, open job); a weld is
 * recorded from here (any working role, open job).
 */
import React from 'react';
import { Plus, Upload } from 'lucide-react';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfAnomalies, usePfDigs, usePfWelds } from '../../../hooks/usePfDigs';
import { fmtNumber, pfDay } from '../../../lib/pipeline-facility';
import { pfDigStatusSkin, pfWeldResultSkin } from '../../../lib/pf-digs';
import type { PfAnomaly, PfDig } from '../../../types/pf-digs';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_PRIMARY, PF_SECONDARY, PfBadge, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';
import { oneCallText } from './DigsScreen';
import { weldColumns } from './WeldsScreen';

function digColumns(chainageUnit: string | null | undefined, showJob: boolean): PfColumn<PfDig>[] {
  return [
    {
      key: 'dig',
      header: 'Dig',
      render: (d) => (
        <>
          <p className="font-mono font-extrabold text-white">{d.digNumber || '—'}</p>
          {showJob && <p className="mt-0.5 text-xs text-fieldiq-muted">{d.jobNumber || '—'}</p>}
        </>
      ),
    },
    { key: 'chainage', header: `Chainage${chainageUnit ? ` (${chainageUnit})` : ''}`, align: 'right', render: (d) => <span className="font-mono">{fmtNumber(d.chainage)}</span> },
    { key: 'onecall', header: 'One-call / GD ticket', render: (d) => oneCallText(d) },
    { key: 'features', header: 'Features', align: 'right', render: (d) => displayValue(d.featureCount ?? undefined) },
    { key: 'repairs', header: 'Repairs', align: 'right', render: (d) => displayValue(d.repairCount ?? undefined) },
    { key: 'excavated', header: 'Excavated', render: (d) => pfDay(d.excavatedOn) },
    { key: 'report', header: 'Report', render: (d) => <span className="font-mono">{d.reportNumber || (d.reportStatus === 'draft' ? 'Draft' : '—')}</span> },
    { key: 'status', header: 'Status', render: (d) => <PfBadge skin={pfDigStatusSkin(d.status)} value={d.status} /> },
  ];
}

function DigList({ filters, showJob, empty, testId }: { filters: { jobId?: string; siteId?: string }; showJob: boolean; empty: React.ReactNode; testId: string }) {
  const openRecord = useOpenRecord();
  const query = usePfDigs(filters);
  const digs = query.data?.data ?? [];
  return (
    <PfRegister
      query={query}
      rows={digs}
      columns={digColumns(query.data?.meta.units?.chainage, showJob)}
      rail={(d) => pfDigStatusSkin(d.status).rail}
      onOpen={(d) => openRecord(`/pipeline-facility/digs/${d.id}`)}
      subject="digs"
      testId={testId}
      minWidth="min-w-[900px]"
      empty={empty}
      card={(d) => (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-extrabold text-white">{d.digNumber}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">
              {showJob ? `${d.jobNumber || '—'} · ` : ''}
              {fmtNumber(d.chainage)} · {oneCallText(d)}
            </p>
          </div>
          <PfBadge skin={pfDigStatusSkin(d.status)} value={d.status} />
        </div>
      )}
    />
  );
}

export function PfJobDigsSection({ jobId, canPlan }: { jobId: string; canPlan: boolean }) {
  const openRecord = useOpenRecord();
  return (
    <PfPanel
      eyebrow="Integrity digs"
      title="Digs on this job"
      testId="pf-job-digs"
      actions={
        canPlan ? (
          <button type="button" className={PF_PRIMARY} data-testid="pf-job-new-dig" onClick={() => openRecord(`/pipeline-facility/jobs/${jobId}/digs/new`)}>
            <Plus className="size-4" aria-hidden="true" /> New dig
          </button>
        ) : undefined
      }
    >
      <DigList filters={{ jobId }} showJob={false} testId="pf-job-dig" empty={<PfEmpty>No digs on this job yet.{canPlan ? ' Plan one with New dig.' : ''}</PfEmpty>} />
    </PfPanel>
  );
}

export function PfSiteDigHistory({ siteId }: { siteId: string }) {
  return (
    <PfPanel eyebrow="Integrity digs" title="Dig history" testId="pf-site-digs">
      <DigList filters={{ siteId }} showJob testId="pf-site-dig" empty={<PfEmpty>No digs at this site yet.</PfEmpty>} />
    </PfPanel>
  );
}

export function PfJobWeldsSection({ jobId, canRecord }: { jobId: string; canRecord: boolean }) {
  const openRecord = useOpenRecord();
  const query = usePfWelds({ jobId });
  const welds = query.data?.data ?? [];
  return (
    <PfPanel
      eyebrow="Welds"
      title="Welds on this job"
      testId="pf-job-welds"
      actions={
        canRecord ? (
          <button type="button" className={PF_PRIMARY} data-testid="pf-job-new-weld" onClick={() => openRecord(`/pipeline-facility/jobs/${jobId}/welds/new`)}>
            <Plus className="size-4" aria-hidden="true" /> New weld
          </button>
        ) : undefined
      }
    >
      <PfRegister
        query={query}
        rows={welds}
        columns={weldColumns(query.data?.meta.units?.chainage)}
        rail={(w) => pfWeldResultSkin(w.ndtResult).rail}
        onOpen={(w) => openRecord(`/pipeline-facility/welds/${w.id}`)}
        subject="welds"
        testId="pf-job-weld"
        minWidth="min-w-[1100px]"
        empty={<PfEmpty>No welds on this job yet.</PfEmpty>}
        card={(w) => (
          <>
            <p className="font-mono text-sm font-extrabold text-white">{w.weldNumber}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">{w.digNumber || 'No dig'} · {w.welderName || '—'} · {pfDay(w.weldDate)}</p>
          </>
        )}
      />
    </PfPanel>
  );
}

/** The site's anomaly list (ILI features), with import and add for the office. */
export function PfSiteAnomaliesPanel({ siteId, canImport }: { siteId: string; canImport: boolean }) {
  const openRecord = useOpenRecord();
  const query = usePfAnomalies({ siteId });
  const rows = query.data?.data ?? [];
  const units = query.data?.meta.units ?? null;
  const undug = rows.filter((a) => !a.digNumber).length;
  const columns: PfColumn<PfAnomaly>[] = [
    { key: 'feature', header: 'Feature', render: (a) => (<><p className="font-mono font-extrabold text-white">{a.featureId || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{a.featureTypeLabel || a.featureType || '—'}</p></>) },
    { key: 'chainage', header: `Chainage${units?.chainage ? ` (${units.chainage})` : ''}`, align: 'right', render: (a) => <span className="font-mono">{fmtNumber(a.chainage)}</span> },
    { key: 'clock', header: 'Clock', render: (a) => <span className="font-mono">{a.clockPosition || '—'}</span> },
    { key: 'depth', header: 'Depth (% WT)', align: 'right', render: (a) => <span className="font-mono">{fmtNumber(a.depthPctWt)}</span> },
    { key: 'ili', header: 'ILI run', render: (a) => (<><p>{a.iliVendor || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{pfDay(a.iliRunDate)}</p></>) },
    { key: 'dig', header: 'Dig', render: (a) => <span className="font-mono">{a.digNumber || '—'}</span> },
  ];
  return (
    <PfPanel
      eyebrow="Integrity"
      title="Anomalies"
      testId="pf-site-anomalies"
      footer={!query.isLoading && rows.length ? `${rows.length} anomal${rows.length === 1 ? 'y' : 'ies'} · ${undug} not yet dug` : undefined}
      actions={
        canImport ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={PF_SECONDARY} onClick={() => openRecord(`/pipeline-facility/anomalies/new?siteId=${siteId}`)}>
              Add anomaly
            </button>
            <button type="button" className={PF_PRIMARY} data-testid="pf-site-import" onClick={() => openRecord(`/pipeline-facility/anomalies/import?siteId=${siteId}`)}>
              <Upload className="size-4" aria-hidden="true" /> Import ILI list
            </button>
          </div>
        ) : undefined
      }
    >
      <PfRegister
        query={query}
        rows={rows}
        columns={columns}
        rail={(a) => (a.digNumber ? 'border-l-fi-blue' : 'border-l-fi-amber')}
        onOpen={(a) => openRecord(`/pipeline-facility/anomalies/${a.id}`)}
        subject="anomalies"
        testId="pf-site-anomaly"
        minWidth="min-w-[860px]"
        empty={<PfEmpty>No anomalies on this site.{canImport ? ' Import the ILI feature list to plan digs.' : ''}</PfEmpty>}
        card={(a) => (
          <>
            <p className="font-mono text-sm font-extrabold text-white">{a.featureId || '—'}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">{a.featureTypeLabel || a.featureType || '—'} · {fmtNumber(a.chainage)} · {a.digNumber || 'not dug'}</p>
          </>
        )}
      />
    </PfPanel>
  );
}
